import { useEffect } from 'react';

/**
 * Allows the scrollable content area inside a FocusedTask drawer to scroll on
 * mobile browsers that have Atlaskit's `react-scrolllock` enabled. We do two
 * things:
 *   1. Add the recognised `data-body-scroll-lock-ignore` attribute so the
 *      global touch-move listener chooses not to `preventDefault()`.
 *   2. Attach `touchstart` and `touchmove` listeners that stop propagation so
 *      the event never reaches the document-level listener in the first place
 *      (defensive-in-depth).
 *
 * @param isOpen – re-run the effect whenever the drawer’s open state changes.
 */
export const useEnableFocusedTaskScroll = isOpen => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const selector = '[data-testid$="-focused-task-content"]';
    let patchedEl = null;
    let rafId;
    const stop = e => {
      // Allow default scrolling on the element itself but keep the event from
      // bubbling to `document` where react-scrolllock cancels it.
      e.stopPropagation();
    };
    const tryAttach = () => {
      const el = document.querySelector(selector);
      if (el) {
        patchedEl = el;
        patchedEl.setAttribute('data-body-scroll-lock-ignore', 'true');
        // Ensure the wrapper itself stretches the full height so that
        // 1) its overflow area spans the whole viewport and
        // 2) scrolling behaves consistently across browsers.
        patchedEl.style.height = '100%';
        // Only attach once per element.
        if (!patchedEl._mpacStopPropagationAttached) {
          patchedEl.addEventListener('touchstart', stop, {
            passive: false
          });
          patchedEl.addEventListener('touchmove', stop, {
            passive: false
          });
          patchedEl._mpacStopPropagationAttached = stop;
        }
      } else {
        // Retry on the next animation frame until the element is in the DOM.
        rafId = window.requestAnimationFrame(tryAttach);
      }
    };
    tryAttach();
    return () => {
      if (rafId !== undefined) {
        window.cancelAnimationFrame(rafId);
      }
      if (patchedEl) {
        const attached = patchedEl._mpacStopPropagationAttached;
        if (attached) {
          patchedEl.removeEventListener('touchstart', attached);
          patchedEl.removeEventListener('touchmove', attached);
          delete patchedEl._mpacStopPropagationAttached;
        }
      }
    };
  }, [isOpen]);
};